import { Card, CardContent } from '@/common/ui/card'
import {
	Users,
	MousePointerClick,
	Gamepad2,
	Clock,
	MessageSquare,
} from 'lucide-react'

import type { FlexMessageAnalyticsOverall } from '../../types'

interface FlexAnalyticsOverviewCardsProps {
	overall: FlexMessageAnalyticsOverall | undefined
	isLoading?: boolean
}

interface KPICardProps {
	title: string
	value: string
	subtitle?: string
	icon: React.ReactNode
	isLoading?: boolean
	highlight?: boolean
}

function KPICard({
	title,
	value,
	subtitle,
	icon,
	isLoading,
	highlight,
}: KPICardProps) {
	if (isLoading) {
		return (
			<Card>
				<CardContent className="p-6">
					<div className="flex items-start justify-between">
						<div className="space-y-2">
							<div className="h-4 w-24 animate-pulse rounded bg-muted" />
							<div className="h-8 w-32 animate-pulse rounded bg-muted" />
							<div className="h-3 w-20 animate-pulse rounded bg-muted" />
						</div>
						<div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card
			className={
				highlight ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20' : ''
			}
		>
			<CardContent className="p-6">
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<p className="text-sm font-medium text-muted-foreground">{title}</p>
						<p className="text-2xl font-bold tracking-tight">{value}</p>
						{subtitle && (
							<p className="text-xs text-muted-foreground">{subtitle}</p>
						)}
					</div>
					<div
						className={`rounded-full p-2.5 ${
							highlight
								? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400'
								: 'bg-muted text-muted-foreground'
						}`}
					>
						{icon}
					</div>
				</div>
			</CardContent>
		</Card>
	)
}

function formatNumber(value: number): string {
	return value.toLocaleString('th-TH')
}

function formatRate(rate: number | undefined): string {
	if (rate === undefined || rate === null) return '0.00%'
	return `${rate.toFixed(2)}%`
}

function formatHours(hours: number | undefined): string {
	if (hours === undefined || hours === null) return '-'
	if (hours < 1) {
		return `${Math.round(hours * 60)} นาที`
	}
	return `${hours.toFixed(1)} ชม.`
}

export default function FlexAnalyticsOverviewCards({
	overall,
	isLoading,
}: FlexAnalyticsOverviewCardsProps) {
	return (
		<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
			{/* Total Recipients */}
			<KPICard
				title="ผู้รับทั้งหมด"
				value={formatNumber(overall?.totalRecipients ?? 0)}
				subtitle={`เปิดดู ${formatNumber(overall?.totalUniqueOpeners ?? 0)} คน`}
				icon={<Users className="h-5 w-5" />}
				isLoading={isLoading}
			/>

			{/* Open Rate */}
			<KPICard
				title="อัตราเปิดดู"
				value={formatRate(overall?.avgOpenRate)}
				subtitle={`เปิดทั้งหมด ${formatNumber(overall?.totalOpens ?? 0)} ครั้ง`}
				icon={<MousePointerClick className="h-5 w-5" />}
				isLoading={isLoading}
				highlight
			/>

			{/* Play Rate */}
			<KPICard
				title="อัตราเล่นเกม"
				value={formatRate(overall?.avgPlayRate)}
				subtitle={`เล่น ${formatNumber(overall?.totalUniquePlayers ?? 0)} คน`}
				icon={<Gamepad2 className="h-5 w-5" />}
				isLoading={isLoading}
			/>

			{/* Avg Time to Open */}
			<KPICard
				title="เวลาเฉลี่ยก่อนเปิด"
				value={formatHours(overall?.avgHoursToOpen)}
				subtitle="หลังจากได้รับข้อความ"
				icon={<Clock className="h-5 w-5" />}
				isLoading={isLoading}
			/>

			{/* Flex Message Count */}
			<KPICard
				title="จำนวน Flex Message"
				value={formatNumber(overall?.flexMessageCount ?? 0)}
				subtitle="ที่ส่งทั้งหมด"
				icon={<MessageSquare className="h-5 w-5" />}
				isLoading={isLoading}
			/>
		</div>
	)
}
